import React, { useEffect, useRef, useState } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import { useTheme } from "../context/ThemeContext"
import { SkeletonCard, SkeletonText, SkeletonButton } from "../components/LoadingSpinner"
import ParticleBackground from "../components/ParticleBackground"
import SpeakingScene from "../components/SpeakingScene"
import ExerciseVocabulary from "../components/ExerciseVocabulary"
import { scrollToTop, scrollResultIntoView } from "../utils/scroll"

function levelLabel(levelNum, t) {
  switch (levelNum) {
    case 1:
      return t("beginner")
    case 2:
      return t("elementary")
    case 3:
      return t("intermediate")
    case 4:
      return t("advanced")
    default:
      return `${t("speakingLevel")} ${levelNum}`
  }
}

function getLevelGradient(levelNum) {
  switch (levelNum) {
    case 1:
      return "from-amber-500 to-orange-600"
    case 2:
      return "from-orange-500 to-amber-700"
    case 3:
      return "from-orange-600 to-primary"
    case 4:
      return "from-primary to-accent"
    default:
      return "from-muted-foreground/70 to-muted-foreground"
  }
}

function getDifficultyColor(level) {
  switch (level) {
    case "Beginner":
      return "from-amber-500 to-orange-600"
    case "Elementary":
      return "from-orange-500 to-amber-600"
    case "Intermediate":
      return "from-orange-500 to-amber-700"
    case "Advanced":
      return "from-primary to-accent"
    default:
      return "from-muted-foreground/70 to-muted-foreground"
  }
}

function getScoreColor(s) {
  if (s >= 80) return "text-primary"
  if (s >= 60) return "text-amber-600 dark:text-amber-400"
  return "text-destructive"
}

function getScoreBgColor(s) {
  if (s >= 80) return "bg-primary/10 border-primary/30"
  if (s >= 60) return "bg-amber-500/10 border-amber-500/30"
  return "bg-destructive/10 border-destructive/30"
}

function getSpeechRecognition() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SR) return null
  const recognition = new SR()
  recognition.lang = "en-US"
  recognition.continuous = true
  recognition.interimResults = true
  recognition.maxAlternatives = 1
  return recognition
}

function formatRecordTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}

function speakText(text) {
  if (!text || !("speechSynthesis" in window)) return
  window.speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = "en-US"
  utterance.rate = 0.95
  window.speechSynthesis.speak(utterance)
}

export default function Speaking() {
  const { t } = useLanguage()
  const { isDark } = useTheme()
  const [view, setView] = useState("picker")
  const [levelFilter, setLevelFilter] = useState("all")
  const [availableLevels, setAvailableLevels] = useState([])
  const [lessons, setLessons] = useState([])
  const [lessonsLoading, setLessonsLoading] = useState(true)
  const [hoveredLessonId, setHoveredLessonId] = useState(null)
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [selectedText, setSelectedText] = useState("")

  const [, setScore] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [aiSession, setAiSession] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [userInput, setUserInput] = useState("")
  const [turnLoading, setTurnLoading] = useState(false)
  const [sessionSummary, setSessionSummary] = useState(null)
  const [isListening, setIsListening] = useState(false)
  const [liveTranscript, setLiveTranscript] = useState("")
  const [recordSeconds, setRecordSeconds] = useState(0)
  const [recordNote, setRecordNote] = useState("")
  const [sessionStartedAt, setSessionStartedAt] = useState(null)

  const recognitionRef = useRef(null)
  const wantListeningRef = useRef(false)
  const transcriptRef = useRef("")
  const restartTimerRef = useRef(null)
  const chatEndRef = useRef(null)
  const shapesRef = useRef(null)
  const lessonsLoadedRef = useRef(false)

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -3}deg) rotateY(${x * 5}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const loadLessons = async (selectedLevel = levelFilter) => {
    if (!lessonsLoadedRef.current) setLessonsLoading(true)
    setError("")
    try {
      const levelParam = selectedLevel !== "all" ? Number(selectedLevel) : null
      const data = await api.listSpeakingExercises(levelParam)
      const exercises = data.exercises || []
      setLessons(exercises)
      lessonsLoadedRef.current = true

      if (selectedLevel === "all") {
        const levels = [...new Set(exercises.map((ex) => ex.level).filter(Boolean))].sort(
          (a, b) => a - b
        )
        setAvailableLevels(levels)
      }
    } catch (e) {
      setError(e.message)
      setLessons([])
    } finally {
      setLessonsLoading(false)
    }
  }

  const loadConversation = async (conversationId) => {
    stopListening()
    setLoading(true)
    setError("")
    setScore(null)
    setAiSession(null)
    setChatMessages([])
    setUserInput("")
    setSessionSummary(null)
    setSessionStartedAt(null)
    scrollToTop()
    try {
      const data = await api.getConversation(conversationId)
      setSelectedConversation(data)
      setView("practice")
      scrollToTop()
    } catch (e) {
      setError(e.message)
      setView("picker")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isListening) return undefined
    setRecordSeconds(0)
    const id = window.setInterval(() => {
      setRecordSeconds((seconds) => seconds + 1)
    }, 1000)
    return () => window.clearInterval(id)
  }, [isListening])

  useEffect(() => {
    loadLessons()
    return () => {
      wantListeningRef.current = false
      if (restartTimerRef.current) window.clearTimeout(restartTimerRef.current)
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch {
          /* ignore */
        }
      }
      if ("speechSynthesis" in window) window.speechSynthesis.cancel()
    }
  }, [])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [chatMessages, turnLoading, sessionSummary])

  const resetAiChat = () => {
    setAiSession(null)
    setChatMessages([])
    setUserInput("")
    setSessionSummary(null)
    setSessionStartedAt(null)
    setScore(null)
    setError("")
    transcriptRef.current = ""
    setLiveTranscript("")
    setRecordSeconds(0)
    stopListening()
    setRecordNote("")
  }

  const stopListening = () => {
    wantListeningRef.current = false
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current)
      restartTimerRef.current = null
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch {
        /* ignore */
      }
    }
    const heard = transcriptRef.current.trim()
    setRecordNote(heard ? "stopped" : isListening ? "empty" : "")
    setIsListening(false)
  }

  const startAiConversation = async () => {
    const convo = selectedConversation?.conversation
    if (!convo?.id) return
    setError("")
    setSubmitting(true)
    resetAiChat()
    try {
      const session = await api.startSpeakingConversation(convo.id)
      if (!session.ai_configured) {
        setError(
          "AI speaking is not configured on the server yet. Ask your teacher to set HF_API_TOKEN."
        )
        return
      }
      setAiSession(session)
      setSessionStartedAt(Date.now())
      const opening = {
        role: "assistant",
        content: session.opening_message,
      }
      setChatMessages([opening])
      speakText(session.opening_message)
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const sendUserTurn = async (text) => {
    const trimmed = (text || "").trim()
    if (!trimmed || !aiSession || turnLoading || isListening) return

    stopListening()
    transcriptRef.current = ""
    setLiveTranscript("")
    setRecordNote("")
    setTurnLoading(true)
    setError("")
    setUserInput("")

    const userMessage = { role: "user", content: trimmed }
    const history = [...chatMessages, userMessage]
    setChatMessages(history)

    try {
      const res = await api.sendSpeakingTurn({
        conversation_id: aiSession.conversation_id,
        lesson_id: aiSession.lesson_id,
        messages: chatMessages,
        user_text: trimmed,
      })
      const assistantMessage = { role: "assistant", content: res.reply }
      setChatMessages([...history, assistantMessage])
      speakText(res.reply)
    } catch (e) {
      setError(e.message)
      setChatMessages(chatMessages)
      setUserInput(trimmed)
    } finally {
      setTurnLoading(false)
    }
  }

  const endAiSession = async () => {
    stopListening()
    if (!aiSession || chatMessages.length < 2) return
    setSubmitting(true)
    setError("")
    try {
      const timeSpent = sessionStartedAt
        ? Math.round((Date.now() - sessionStartedAt) / 1000)
        : 120
      const res = await api.endSpeakingConversation({
        conversation_id: aiSession.conversation_id,
        lesson_id: aiSession.lesson_id,
        messages: chatMessages,
        time_spent: timeSpent,
      })
      setScore(res.score)
      setSessionSummary(res)
      scrollResultIntoView()
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const startListening = () => {
    if (isListening || turnLoading || submitting) return

    const recognition = getSpeechRecognition()
    if (!recognition) {
      setError(t("speakingRecordUnsupported"))
      return
    }

    if ("speechSynthesis" in window) window.speechSynthesis.cancel()

    transcriptRef.current = ""
    setLiveTranscript("")
    setRecordNote("")
    setError("")
    wantListeningRef.current = true
    recognitionRef.current = recognition

    recognition.onresult = (event) => {
      let spoken = ""
      for (let index = 0; index < event.results.length; index += 1) {
        spoken += event.results[index][0]?.transcript || ""
      }
      const next = spoken.replace(/\s+/g, " ").trim()
      transcriptRef.current = next
      setLiveTranscript(next)
      setUserInput(next)
    }
    recognition.onerror = (event) => {
      const code = event?.error || ""
      if (code === "aborted" || code === "no-speech") return
      wantListeningRef.current = false
      setIsListening(false)
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError(t("speakingMicBlocked"))
        return
      }
      setError(t("speakingCouldNotHear"))
    }
    recognition.onend = () => {
      if (!wantListeningRef.current || recognitionRef.current !== recognition) {
        setIsListening(false)
        return
      }
      restartTimerRef.current = window.setTimeout(() => {
        if (!wantListeningRef.current || recognitionRef.current !== recognition) return
        try {
          recognition.start()
        } catch {
          /* already running */
        }
      }, 200)
    }

    try {
      recognition.start()
      setIsListening(true)
    } catch {
      wantListeningRef.current = false
      setIsListening(false)
      setError(t("speakingCouldNotHear"))
    }
  }

  const backToLessons = () => {
    resetAiChat()
    setView("picker")
    setSelectedConversation(null)
    scrollToTop()
  }

  const levelOptions = [
    { key: "all", label: t("allLevels"), color: "from-muted-foreground/70 to-muted-foreground" },
    ...availableLevels.map((levelNum) => ({
      key: String(levelNum),
      label: `${t("speakingLevel")} ${levelNum}`,
      sublabel: levelLabel(levelNum, t),
      color: getLevelGradient(levelNum),
    })),
  ]

  const sceneShell = (children) => (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
      <SpeakingScene layerRef={shapesRef} />
      <div
        className="fixed inset-0 pointer-events-none z-[1] opacity-60"
        style={{
          background: isDark
            ? "radial-gradient(ellipse 70% 45% at 50% 40%, rgb(34 211 238 / 0.06), transparent 55%), radial-gradient(ellipse 50% 40% at 15% 85%, rgb(var(--primary-rgb) / 0.08), transparent)"
            : "radial-gradient(ellipse 70% 45% at 50% 40%, rgb(34 211 238 / 0.1), transparent 55%), radial-gradient(ellipse 50% 40% at 20% 90%, rgb(var(--primary-rgb) / 0.07), transparent)",
        }}
        aria-hidden
      />
      <div key={view} className="relative z-10 speaking-scene view-stage">{children}</div>
    </div>
  )

  if (lessonsLoading || (view === "practice" && loading && !selectedConversation)) {
    return sceneShell(
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="animate-pulse space-y-8">
          <div className="text-center space-y-4 pt-8">
            <SkeletonText lines={2} className="max-w-md mx-auto" />
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            <SkeletonButton className="w-24" />
            <SkeletonButton className="w-24" />
            <SkeletonButton className="w-24" />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <SkeletonCard className="h-44 rounded-2xl" />
            <SkeletonCard className="h-44 rounded-2xl" />
            <SkeletonCard className="h-44 rounded-2xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error && view === "picker" && lessons.length === 0) {
    return sceneShell(
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="speaking-panel-3d bg-card/90 backdrop-blur-md rounded-2xl shadow-xl border border-border p-6 max-w-md w-full text-center">
          <h3 className="text-base font-semibold text-foreground mb-2">{t("somethingWentWrong")}</h3>
          <p className="text-destructive mb-4 text-sm">{error}</p>
          <button
            type="button"
            onClick={() => loadLessons()}
            className="button-glow bg-gradient-to-r from-primary to-accent text-primary-foreground px-6 py-2 rounded-xl hover:opacity-95 transition-all text-sm font-medium"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  if (view === "picker") {
    return sceneShell(
      <div className="speaking-picker max-w-3xl mx-auto px-3 sm:px-4 lg:px-6 w-full min-w-0 py-6 md:py-10">
        <header className="speaking-hero-minimal speaking-picker-hero mb-6 md:mb-8 text-center">
          <h1 className="speaking-hero-title">{t("speaking")}</h1>
          <div className="speaking-hero-accent speaking-picker-accent" aria-hidden />
          <p className="speaking-hero-subtitle mx-auto">{t("speakingPickShelf")}</p>
        </header>

        <section className="speaking-lounge-filters mb-6 sm:mb-8">
          <p className="speaking-lounge-label">{t("speakingChooseLevel")}</p>
          <div className="speaking-lounge-chips">
            {levelOptions.map((level) => {
              const isActive = levelFilter === level.key
              return (
                <button
                  key={level.key}
                  type="button"
                  onClick={() => {
                    setLevelFilter(level.key)
                    loadLessons(level.key)
                  }}
                  className={`speaking-lounge-chip ${isActive ? "speaking-lounge-chip--active" : ""}`}
                >
                  <span className="speaking-lounge-chip-label">{level.label}</span>
                  {level.sublabel && (
                    <span className="speaking-lounge-chip-sub">{level.sublabel}</span>
                  )}
                </button>
              )
            })}
          </div>
        </section>

        <section className="w-full min-w-0">
          <div className="speaking-scene-list-header">
            <h2 className="speaking-scene-list-title">{t("speakingChooseLesson")}</h2>
            <p className="speaking-scene-list-count">
              {lessons.length}{" "}
              {lessons.length === 1 ? t("speakingLesson") : t("speakingLessons")}
            </p>
          </div>

          {error && <p className="text-sm text-destructive mb-4">{error}</p>}

          <div className="speaking-scene-list">
            {lessons.map((lesson) => {
              const lessonKey = lesson.id || lesson.title
              const isHovered = hoveredLessonId === lessonKey
              const preview =
                lesson.instructions?.trim() ||
                `${lesson.lines_count} ${t("speakingLines")}`
              return (
                <button
                  key={lessonKey}
                  type="button"
                  onMouseEnter={() => setHoveredLessonId(lessonKey)}
                  onMouseLeave={() => setHoveredLessonId(null)}
                  onFocus={() => setHoveredLessonId(lessonKey)}
                  onBlur={() => setHoveredLessonId(null)}
                  onClick={() => loadConversation(lesson.id)}
                  className={`speaking-convo-card speaking-panel-3d group ${
                    isHovered ? "speaking-convo-card--active" : ""
                  }`}
                >
                  <div className="speaking-convo-stage">
                    <div className="speaking-convo-avatars" aria-hidden>
                      <span className="speaking-convo-avatar speaking-convo-avatar--ai">AI</span>
                      <span className="speaking-convo-avatar speaking-convo-avatar--you">
                        {t("speakingYou").charAt(0)}
                      </span>
                    </div>

                    <div className="speaking-convo-thread min-w-0 flex-1">
                      <div className="speaking-convo-head">
                        <h3 className="speaking-convo-title">{lesson.title}</h3>
                        <span className="speaking-convo-mic" aria-hidden>
                          <span className={`speaking-convo-mic-dot ${isHovered ? "speaking-convo-mic-dot--live" : ""}`} />
                          {t("speakingAiChat")}
                        </span>
                      </div>

                      <div className="speaking-convo-bubbles">
                        <div className="speaking-convo-bubble speaking-convo-bubble--ai">
                          <span className="speaking-convo-bubble-label">{t("speakingAiPartner")}</span>
                          <p>{preview}</p>
                        </div>
                        <div className="speaking-convo-bubble speaking-convo-bubble--you">
                          <span className="speaking-convo-bubble-label">{t("speakingYou")}</span>
                          <p>
                            {t("speakingLevel")} {lesson.level} · {lesson.difficulty} · ~
                            {lesson.estimated_duration} {t("speakingMin")}
                          </p>
                        </div>
                      </div>

                      <div className="speaking-convo-foot">
                        <span className="speaking-convo-stat">
                          {lesson.lines_count} {t("speakingLines")}
                        </span>
                        {lesson.vocabulary_count > 0 && (
                          <span className="speaking-convo-stat">
                            {lesson.vocabulary_count} {t("speakingVocabWords")}
                          </span>
                        )}
                        <span className="speaking-convo-cta">
                          {t("speakingStartPractice")}
                          <span aria-hidden>→</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {lessons.length === 0 && !error && (
            <div className="speaking-panel-3d speaking-scene-list-empty">
              {t("speakingNoLessons")}
            </div>
          )}
        </section>
      </div>
    )
  }

  const convo = selectedConversation?.conversation

  return sceneShell(
    <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 w-full min-w-0 pb-8 md:pb-10">
      <header className="speaking-hero-minimal mb-6 md:mb-8">
        <h1 className="speaking-hero-title">{convo?.title || t("speaking")}</h1>
        <div className="speaking-hero-accent" aria-hidden />
      </header>

      <div className="mb-6">
        <button
          type="button"
          onClick={backToLessons}
          className="text-sm font-medium text-primary hover:underline"
        >
          ← {t("speakingBackToLessons")}
        </button>
      </div>

      {loading && (
        <div className="speaking-panel-3d rounded-2xl border border-border bg-card/70 p-8 text-center text-muted-foreground text-sm">
          {t("speakingLoadingConversation")}
        </div>
      )}

      {!loading && error && !convo && (
        <div className="speaking-panel-3d rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-destructive text-sm mb-4">{error}</p>
          <button type="button" onClick={backToLessons} className="text-sm text-primary hover:underline">
            {t("speakingBackToLessons")}
          </button>
        </div>
      )}

      {!loading && convo && (
        <div className="space-y-6 md:space-y-8 w-full min-w-0">
          <section className="speaking-panel-3d speaking-section-card p-5 sm:p-6 md:p-8 w-full min-w-0">
            <h2 className="speaking-section-title">{t("speakingSessionBrief")}</h2>
            <div className="speaking-brief-surface mt-4 w-full min-w-0">
              <p className="speaking-brief-text">{convo.instructions}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="speaking-meta-badge">
                  {t("speakingLevel")} {convo.level}
                </span>
                {convo.difficulty && (
                  <span
                    className={`speaking-meta-badge bg-gradient-to-r ${getDifficultyColor(convo.difficulty)} text-white border-transparent`}
                  >
                    {convo.difficulty}
                  </span>
                )}
                <span className="speaking-meta-badge">
                  ~{convo.estimated_duration} {t("speakingMin")}
                </span>
              </div>
            </div>
          </section>

          <section className="speaking-panel-3d speaking-section-card p-4 sm:p-6 md:p-8 w-full min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <h3 className="speaking-section-title normal-case tracking-normal text-base">
                  {t("speakingPracticeWithAi")}
                </h3>
                <p className="speaking-section-desc mt-1">{t("speakingPracticeWithAiHint")}</p>
              </div>
              {aiSession && (
                <span className="speaking-meta-badge border-sky-500/25 bg-sky-500/10 text-sky-800 dark:text-sky-200">
                  {t("speakingYou")}: {t("speakingPerson")} {aiSession.student_role} ·{" "}
                  {t("speakingAiPartner")}: {t("speakingPerson")} {aiSession.ai_role}
                </span>
              )}
            </div>

            {!aiSession && (
              <button
                type="button"
                onClick={startAiConversation}
                disabled={submitting}
                className="button-glow w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold disabled:opacity-50"
              >
                {submitting ? t("speakingStarting") : t("speakingStartAiConversation")}
              </button>
            )}

            {aiSession && (
              <div className="space-y-4">
                <div className="speaking-chat-panel space-y-3">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`speaking-chat-bubble ${
                          msg.role === "user"
                            ? "speaking-chat-bubble--user"
                            : "speaking-chat-bubble--ai"
                        }`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                          {msg.role === "user" ? t("speakingYou") : t("speakingAiPartner")}
                        </p>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {turnLoading && (
                    <p className="text-sm text-muted-foreground animate-pulse">
                      {t("speakingAiThinking")}
                    </p>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {!sessionSummary && (
                  <div className="speaking-composer">
                    <div className={`speaking-recorder ${isListening ? "is-recording" : ""}`}>
                      <button
                        type="button"
                        onClick={isListening ? stopListening : startListening}
                        disabled={turnLoading || submitting}
                        aria-pressed={isListening}
                        className={`speaking-record-btn ${isListening ? "is-recording" : ""}`}
                      >
                        {isListening ? (
                          <svg className="speaking-record-icon" viewBox="0 0 24 24" aria-hidden>
                            <rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" />
                          </svg>
                        ) : (
                          <svg className="speaking-record-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                            <path strokeWidth="1.8" strokeLinecap="round" d="M12 4a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V7a3 3 0 0 0-3-3z" />
                            <path strokeWidth="1.8" strokeLinecap="round" d="M6.5 11a5.5 5.5 0 0 0 11 0M12 16.5V20" />
                          </svg>
                        )}
                        {isListening ? t("speakingStopRecording") : t("speakingStartRecording")}
                      </button>
                      <p className="speaking-record-status" aria-live="polite">
                        {isListening
                          ? t("speakingRecordingHint")
                          : recordNote === "stopped"
                            ? t("speakingRecordingStopped")
                            : recordNote === "empty"
                              ? t("speakingRecordingEmpty")
                              : t("speakingRecordIdleHint")}
                      </p>
                      {isListening && (
                        <div className="speaking-record-live">
                          <div className="speaking-record-meter" aria-hidden>
                            <span className="speaking-record-timer tabular-nums">{formatRecordTime(recordSeconds)}</span>
                            <span className="speaking-record-bars">
                              {Array.from({ length: 7 }).map((_, index) => (
                                <span key={index} style={{ "--i": index }} />
                              ))}
                            </span>
                          </div>
                          <p className="speaking-record-caption" aria-live="polite">
                            {liveTranscript || t("speakingListeningForWords")}
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="speaking-record-or">{t("speakingOrType")}</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault()
                            sendUserTurn(userInput)
                          }
                        }}
                        disabled={turnLoading || submitting || isListening}
                        placeholder={t("speakingTypeReply")}
                        aria-label={t("speakingTypeReply")}
                        className="speaking-input flex-1 disabled:opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => sendUserTurn(userInput)}
                        disabled={!userInput.trim() || turnLoading || submitting || isListening}
                        className="speaking-action-btn speaking-action-btn--primary"
                      >
                        {t("speakingSend")}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
                  {!sessionSummary && (
                    <button
                      type="button"
                      onClick={endAiSession}
                      disabled={chatMessages.length < 2 || submitting || turnLoading}
                      className="button-glow px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold text-sm disabled:opacity-50"
                    >
                      {submitting ? t("speakingSaving") : t("speakingEndSession")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => loadConversation(convo.id)}
                    disabled={loading || submitting}
                    className="speaking-action-btn text-sm disabled:opacity-50"
                  >
                    {t("speakingNewSession")}
                  </button>
                </div>
              </div>
            )}

            {error && <p className="text-sm text-destructive mt-4">{error}</p>}
          </section>

          <details className="speaking-panel-3d speaking-section-card p-4 sm:p-6 w-full min-w-0">
            <summary className="speaking-section-title normal-case tracking-normal text-base cursor-pointer">
              {t("speakingReferenceScript")}
            </summary>
            <div className="space-y-3 mt-4">
              {convo.conversation.map((line, idx) => (
                <div
                  key={idx}
                  className={`speaking-dialogue-line ${
                    line.speaker === "A" ? "speaking-dialogue-line--a" : "speaking-dialogue-line--b"
                  }`}
                >
                  <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                    {t("speakingPerson")} {line.speaker}
                  </p>
                  <p
                    className="text-foreground text-sm leading-relaxed select-text"
                    onMouseUp={() => {
                      const picked = window.getSelection()?.toString().trim()
                      if (picked) setSelectedText(picked)
                    }}
                  >
                    {line.text}
                  </p>
                </div>
              ))}
            </div>
          </details>

          <ExerciseVocabulary
            skill="speaking"
            lessonId={selectedConversation?.lesson_id || aiSession?.lesson_id}
            lessonTitle={convo.title}
            sourceRef={convo.id}
            selectedText={selectedText}
            onClearSelection={() => setSelectedText("")}
            contextText={(convo.conversation || []).map((line) => line.text).join(" ")}
            suggestions={convo.vocabulary || []}
          />

          {sessionSummary && (
            <section
              className={`speaking-panel-3d speaking-section-card ui-reveal p-5 md:p-6 ${getScoreBgColor(sessionSummary.score)}`}
            >
              <div className="text-center mb-4">
                <h3 className="speaking-section-title normal-case tracking-normal text-base mb-1">
                  {t("speakingSessionComplete")}
                </h3>
                <p
                  className={`text-3xl md:text-4xl font-bold tabular-nums ${getScoreColor(sessionSummary.score)}`}
                >
                  {Math.round(sessionSummary.score)}% {t("score")}
                </p>
                {sessionSummary.submitted && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                    {t("speakingSavedToAccount")}
                  </p>
                )}
              </div>
              {sessionSummary.overall_comment && (
                <p className="text-sm text-foreground leading-relaxed mb-4">
                  {sessionSummary.overall_comment}
                </p>
              )}
              {sessionSummary.notice && (
                <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
                  {sessionSummary.notice}
                </p>
              )}
              {sessionSummary.strengths?.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                    {t("speakingStrengths")}
                  </h4>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {sessionSummary.strengths.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {sessionSummary.improvements?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
                    {t("speakingHowToImprove")}
                  </h4>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {sessionSummary.improvements.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  )
}
